// All database access in one place. Security is enforced by Postgres RLS, not here.
import { supabase } from './supabase.js';

const must = ({ data, error }) => { if (error) throw new Error(error.message); return data; };

export const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

// ---------- Session / profile ----------
export async function getMyProfile(userId) {
  return must(await supabase.from('profiles').select('*').eq('id', userId).maybeSingle());
}
export async function getConfig() {
  const rows = must(await supabase.from('app_config').select('key, value'));
  return Object.fromEntries(rows.map(r => [r.key, r.value]));
}

// ---------- Locations ----------
export async function listLocations({ includeInactive = false } = {}) {
  let q = supabase.from('locations').select('*').order('is_depot', { ascending: false }).order('name');
  if (!includeInactive) q = q.eq('active', true);
  return must(await q);
}
export async function saveLocation(loc) {
  const { id, ...fields } = loc;
  const q = id
    ? supabase.from('locations').update(fields).eq('id', id).select().single()
    : supabase.from('locations').insert(fields).select().single();
  return must(await q);
}
export async function setLocationActive(id, active) {
  return must(await supabase.from('locations').update({ active }).eq('id', id));
}
/** Import from Excel: update rows whose name matches (case-insensitive), insert the rest. */
export async function importLocations(rows) {
  const existing = await listLocations();
  const byName = new Map(existing.map(l => [l.name.toLowerCase(), l]));
  let updated = 0, inserted = 0;
  for (const r of rows) {
    const hit = byName.get(r.name.toLowerCase());
    if (r.is_depot && !hit?.is_depot) {
      const depot = existing.find(l => l.is_depot);
      if (depot) { r.is_depot = false; } // never silently move the depot
    }
    if (hit) { await saveLocation({ ...r, id: hit.id }); updated++; }
    else { await saveLocation(r); inserted++; }
  }
  return { updated, inserted };
}

// ---------- People ----------
export async function listProfiles() {
  return must(await supabase.from('profiles').select('*').order('role').order('full_name'));
}
export async function updateProfile(id, fields) {
  return must(await supabase.from('profiles').update(fields).eq('id', id));
}
export async function adminUsers(body) {
  const { data, error } = await supabase.functions.invoke('admin-users', { body });
  if (error) {
    let msg = error.message;
    try { msg = (await error.context.json()).error || msg; } catch { /* keep generic */ }
    throw new Error(msg);
  }
  return data;
}

// ---------- Plans ----------
export async function createPlan({ plan_date, depart_min, unload_min }) {
  return must(await supabase.from('plans').insert({ plan_date, depart_min, unload_min }).select().single());
}
export async function savePlan(planId, routes, publish) {
  return must(await supabase.rpc('save_plan', { p_plan: planId, p_routes: routes, p_publish: publish }));
}
export async function listPlans(date) {
  return must(await supabase.from('plans').select('*').eq('plan_date', date).order('created_at'));
}
export async function setPlanStatus(id, status) {
  return must(await supabase.from('plans').update({ status }).eq('id', id));
}
export async function deletePlan(id) {
  return must(await supabase.from('plans').delete().eq('id', id));
}
/** Routes (+stops +locations) for the given plans. Drivers automatically get only their own (RLS). */
export async function routesForPlans(planIds) {
  if (!planIds.length) return [];
  return must(await supabase.from('routes')
    .select('*, driver:profiles(full_name), stops:route_stops(*, location:locations(name, address, lat, lng))')
    .in('plan_id', planIds)
    .order('van_no'))
    .map(r => ({ ...r, stops: [...r.stops].sort((a, b) => a.seq - b.seq) }));
}
export async function myRoutes(date) {
  const plans = must(await supabase.from('plans').select('id, depart_min, unload_min, status').eq('plan_date', date).neq('status', 'draft'));
  const routes = await routesForPlans(plans.map(p => p.id));
  const byId = new Map(plans.map(p => [p.id, p]));
  return routes.map(r => ({ ...r, plan: byId.get(r.plan_id) }));
}
export async function markStop(stopId, status, note) {
  return must(await supabase.rpc('mark_stop', { p_stop: stopId, p_status: status, p_note: note ?? null }));
}
/** Live updates of stop status (dispatcher board). Returns an unsubscribe function. */
export function watchStops(onChange) {
  const ch = supabase.channel('stops')
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'route_stops' }, p => onChange(p.new))
    .subscribe();
  return () => supabase.removeChannel(ch);
}
